import type { CustomerStory } from '../../types/story'

const IMG = 'https://cdn.magicpatterns.com/patterns/generated-images/'

export const otherStories: CustomerStory[] = [
  {
    slug: 'yeet-coffee',
    business: 'YEET Coffee',
    owner: 'Héritier Muhire',
    role: 'Founder',
    industry: 'food-beverage',
    location: 'Kimihurura, Kigali',
    image: `${IMG}c04828fc-7f33-4022-bb39-af6bdb618150.jpg`,
    alt: 'Héritier Muhire behind the counter at YEET Coffee',
    products: ['sales-pos', 'customers', 'reports'],
    plan: 'Starter',
    staff: '5 staff',
    since: '2024',
    headline: 'How a Kigali coffee shop stopped chasing mobile money payments',
    result: '[X] hrs saved every week',
    summary: 'Every payment now matches its sale automatically — no more scrolling through SMS.',
    quote: 'With Kayko, I can follow the growth of YEET Coffee without the usual stress.',
    results: [
      { value: '[X] hrs', label: 'saved each week on payments' },
      { value: '[X]%', label: 'of sales by mobile money' },
      { value: '[X]%', label: 'more regulars returning' },
    ],
    challenge: ['Most customers paid by mobile money, and Héritier spent evenings matching SMS messages to sales.'],
    solution: ['Kayko records every payment against its receipt, whatever the method, and shows totals by method at closing.'],
    outcome: ['Cash-up is now a two-minute check, and Héritier uses customer history to reward his regulars.'],
    inlineImage: {
      src: `${IMG}bf8c75d0-c0c3-451c-b8e2-c025c368382a.jpg`,
      alt: 'Customer paying at the counter',
      caption: 'Customers pay their way, and every payment is matched automatically.',
    },
    screenshot: { visual: { kind: 'screen', id: 'payments' }, caption: 'Payments by method, matched to every sale.' },
    video: { duration: '1:48' },
  },
  {
    slug: 'mizero-cafe-bakery',
    business: 'Mizero Café & Bakery',
    owner: 'Jean-Paul Niyonzima',
    role: 'Owner',
    industry: 'food-beverage',
    location: 'Kimihurura, Kigali',
    image: `${IMG}2c23e8d6-e530-4eee-9f47-5e53b680c437.jpg`,
    alt: 'Jean-Paul Niyonzima in his café and bakery',
    products: ['sales-pos', 'reports', 'expenses'],
    plan: 'Starter',
    staff: '8 staff',
    since: '2023',
    headline: 'How a café and bakery serves more customers every morning',
    result: '[X]% more customers served',
    summary: 'Faster checkout cleared the morning queue and daily reports showed what sells.',
    quote: 'I check my profit every evening on my phone.',
    results: [
      { value: '[X]%', label: 'more customers served' },
      { value: '[X] sec', label: 'average checkout' },
      { value: '[X]%', label: 'higher profit' },
    ],
    challenge: ['The morning rush meant long queues, and some customers left before being served.'],
    solution: ['A tablet till with quick product buttons and mobile money cut the time per order.'],
    outcome: ['The queue moves faster, and daily reports show which pastries to bake more of.'],
    inlineImage: {
      src: 'https://cdn.magicpatterns.com/uploads/um4PVUwRMuB6Dq6iSx7Up4/ChatGPT_Image_Sep_26,_2026,_04_09_05_PM-4.png',
      alt: 'Serving a plated order across the counter',
      caption: 'Orders move from counter to kitchen without paper.',
    },
    screenshot: { visual: { kind: 'screen', id: 'checkout' }, caption: 'Quick checkout for the morning rush.' },
  },
  {
    slug: 'ikawa-corner-cafe',
    business: 'Ikawa Corner Café',
    owner: 'Diane Uwase',
    role: 'Owner',
    industry: 'food-beverage',
    location: 'Kacyiru, Kigali',
    image: `${IMG}1d46ac71-ebbe-43ce-b496-ca33cb099f31.jpg`,
    alt: 'Diane Uwase smiling behind her café counter',
    products: ['inventory', 'sales-pos', 'expenses'],
    plan: 'Starter',
    staff: '6 staff',
    since: '2024',
    headline: 'How a neighbourhood café cut its food waste',
    result: '[X]% less food waste',
    summary: 'Ingredient tracking helped Diane buy what she needs and stop running out at lunch.',
    quote: 'I finally know which dishes carry the café.',
    results: [
      { value: '[X]%', label: 'less food waste' },
      { value: '[X]', label: 'dishes with known margins' },
      { value: '[X]', label: 'lunch stock-outs last month' },
    ],
    challenge: ['Diane bought ingredients by instinct — too much of some, not enough of others.'],
    solution: ['Each dish is linked to its ingredients, so stock updates with every sale and alerts come early.'],
    outcome: ['Market runs are planned, waste is down, and the menu is priced on real costs.'],
    inlineImage: {
      src: 'https://cdn.magicpatterns.com/uploads/um4PVUwRMuB6Dq6iSx7Up4/ChatGPT_Image_Sep_26,_2026,_04_09_05_PM-4.png',
      alt: 'Serving a lunch order at the counter',
      caption: 'Ingredient stock updates with every order.',
    },
    screenshot: {
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Kitchen stock',
          badge: { label: '2 low', tone: 'warning' },
          rows: [
            { label: 'Chicken (kg)', value: '3.5 kg', pct: 18, tone: 'negative' },
            { label: 'Potatoes (kg)', value: '12 kg', pct: 30, tone: 'warning' },
            { label: 'Rice (kg)', value: '28 kg', pct: 72 },
          ],
        },
      },
      caption: 'Ingredient alerts arrive before lunch service, not during it.',
    },
    video: { duration: '2:02' },
  },
  {
    slug: 'kinigi-hills-guesthouse',
    business: 'Kinigi Hills Guesthouse',
    owner: 'Patrick Nshimiyimana',
    role: 'Owner',
    industry: 'hospitality',
    location: 'Musanze',
    image: `${IMG}afacccfd-cb4d-44d3-aec6-9b065b1fefd9.jpg`,
    alt: 'Patrick Nshimiyimana at his guesthouse reception',
    products: ['sales-pos', 'customers', 'reports'],
    plan: 'Growth',
    staff: '12 staff',
    since: '2024',
    headline: 'How a Musanze guesthouse stopped losing room extras',
    result: '[X]% more revenue from extras',
    summary: 'Every meal and drink now lands on the right guest bill.',
    quote: 'Checkout takes two minutes, and nothing is forgotten.',
    results: [
      { value: '[X]%', label: 'more revenue from extras' },
      { value: '[X] min', label: 'average checkout' },
      { value: '[X]', label: 'unpaid guest bills' },
    ],
    challenge: ['Bar and restaurant charges were written on paper and often missed at checkout.'],
    solution: ['Each guest has one bill in Kayko, and staff add meals, drinks, and laundry as they happen.'],
    outcome: ['Checkout is fast and complete, and Patrick sees monthly profit for the property.'],
    inlineImage: {
      src: 'https://cdn.magicpatterns.com/uploads/kif38iqadZcEesbQLKpsTR/ChatGPT_Image_Sep_26,_2026,_04_09_03_PM-2.png',
      alt: 'Guest checking in at reception',
      caption: 'Every guest bill starts at check-in.',
    },
    screenshot: {
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Room 7 · J. Mugisha',
          rows: [
            { label: 'Room (3 nights)', value: 'RWF 150,000' },
            { label: 'Dinner × 2', value: 'RWF 24,000' },
            { label: 'Bar', value: 'RWF 11,500' },
          ],
          footer: { label: 'Balance due', value: 'RWF 135,500' },
        },
      },
      caption: 'One bill per guest, from arrival to checkout.',
    },
  },
  {
    slug: 'ubuzima-pharmacy',
    business: 'Ubuzima Pharmacy',
    owner: 'Alice Uwamahoro',
    role: 'Pharmacist & Owner',
    industry: 'healthcare',
    location: 'Nyamirambo, Kigali',
    image: `${IMG}290df7a2-5654-48ae-b59a-6a81a29ab1f5.jpg`,
    alt: 'Alice Uwamahoro in her pharmacy',
    products: ['inventory', 'purchases', 'sales-pos'],
    plan: 'Starter',
    staff: '4 staff',
    since: '2023',
    headline: 'How a Nyamirambo pharmacy stopped throwing away expired stock',
    result: '[X]% less expired stock',
    summary: 'Batch and expiry tracking warns Alice early, so medicines are sold before they expire.',
    quote: 'Now Kayko tells us early, and we act before it’s too late.',
    results: [
      { value: '[X]%', label: 'less stock lost to expiry' },
      { value: '[X]', label: 'essential medicines out of stock' },
      { value: '[X]%', label: 'of patient balances tracked' },
    ],
    challenge: ['Expired medicines were found on the shelf too late, and essentials sometimes ran out.'],
    solution: ['Batches and expiry dates are recorded on arrival, with alerts weeks in advance and reorder levels for essentials.'],
    outcome: ['Waste is down, essentials stay in stock, and patients trust the pharmacy to have what they need.'],
    inlineImage: {
      src: 'https://cdn.magicpatterns.com/uploads/iDUc4UaB1F5bDm8tDia9dE/ChatGPT_Image_Sep_26,_2026,_04_09_04_PM-3.png',
      alt: 'Serving a customer at the pharmacy counter',
      caption: 'Every sale issues an EBM receipt.',
    },
    screenshot: {
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Expiring soon',
          badge: { label: '3 items', tone: 'warning' },
          rows: [
            { label: 'Amoxicillin 500mg', sublabel: 'Batch A21', value: '18 days', tone: 'negative' },
            { label: 'Paracetamol syrup', sublabel: 'Batch P07', value: '41 days', tone: 'warning' },
            { label: 'ORS sachets', sublabel: 'Batch O33', value: '58 days' },
          ],
        },
      },
      caption: 'Expiry alerts give Alice weeks to act.',
    },
  },
  {
    slug: 'mutoni-beauty-studio',
    business: 'Mutoni Beauty Studio',
    owner: 'Grace Mutoni',
    role: 'Owner',
    industry: 'services',
    location: 'Kimironko, Kigali',
    image: `${IMG}5711f857-bdbd-403b-9f97-8abca4b7ec9b.jpg`,
    alt: 'Grace Mutoni in her beauty studio',
    products: ['sales-pos', 'customers', 'reports'],
    plan: 'Starter',
    staff: '5 staff',
    since: '2024',
    headline: 'How a beauty studio got paid for every service',
    result: '[X]% less money owed',
    summary: 'Client balances and SMS reminders helped Grace collect what she’s owed.',
    quote: 'My regulars actually settle their balances now.',
    results: [
      { value: '[X]%', label: 'less money owed by clients' },
      { value: '[X]%', label: 'more revenue from extras' },
      { value: '[X] min', label: 'to close the day' },
    ],
    challenge: ['Extras went uncharged, and regulars who promised to pay later often didn’t.'],
    solution: ['Every service and product goes on one bill, and client balances are tracked with one-tap SMS reminders.'],
    outcome: ['Grace collects more of what she earns and knows which services bring in the most.'],
    inlineImage: {
      src: 'https://cdn.magicpatterns.com/uploads/6bTCAA9T5mAFwtdRuNtBvU/ChatGPT_Image_Sep_26,_2026,_04_09_11_PM-2.png',
      alt: 'Meeting a client at the studio',
      caption: 'Client history helps every stylist give personal service.',
    },
    screenshot: {
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Client balances',
          rows: [
            { label: 'Yvette K.', value: 'RWF 16,000', tone: 'warning' },
            { label: 'Grace I.', value: 'RWF 8,000' },
            { label: 'Diane U.', value: 'Settled', tone: 'positive' },
          ],
          action: 'Send reminders',
        },
      },
      caption: 'Balances and reminders in one place.',
    },
  },
  {
    slug: 'habimana-furniture-works',
    business: 'Habimana Furniture Works',
    owner: 'Eric Habimana',
    role: 'Founder',
    industry: 'manufacturing',
    location: 'Gikondo, Kigali',
    image: `${IMG}66b3008d-3a38-45cd-9a5b-45b7d3e19ed6.jpg`,
    alt: 'Eric Habimana in his furniture workshop',
    products: ['inventory', 'purchases', 'customers'],
    plan: 'Growth',
    staff: '14 staff',
    since: '2023',
    headline: 'How a furniture workshop learned what every chair really costs',
    result: '[X]% higher margin',
    summary: 'Real material costs helped Eric reprice his products with confidence.',
    quote: 'Now I price every order knowing exactly what the wood and varnish cost me.',
    results: [
      { value: '[X]%', label: 'higher margin' },
      { value: '[X]', label: 'production stops from missing materials' },
      { value: '[X]×', label: 'faster balance collection' },
    ],
    challenge: ['Material prices rose every month, but Eric’s prices were based on old guesses.'],
    solution: ['Purchases record what every supplier charges, and materials are tracked from delivery to finished goods.'],
    outcome: ['Eric repriced his range on real costs and collects customer deposits and balances on time.'],
    inlineImage: {
      src: 'https://cdn.magicpatterns.com/uploads/c8Nf8XJGRxSAWm6B5i5fj7/ChatGPT_Image_Sep_26,_2026,_04_09_09_PM-1.png',
      alt: 'Inspecting finished furniture in the workshop',
      caption: 'Finished goods move into stock as each batch is completed.',
    },
    screenshot: {
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Purchase history · Planks',
          rows: [
            { label: 'Nyabugogo Timber', sublabel: '12 Sep', value: 'RWF 6,500/pc' },
            { label: 'Nyabugogo Timber', sublabel: '14 Aug', value: 'RWF 6,000/pc' },
            { label: 'Musanze Wood Co.', sublabel: '2 Jul', value: 'RWF 5,800/pc' },
          ],
          footer: { label: 'Change (3 months)', value: '+12%' },
        },
      },
      caption: 'Supplier price history revealed rising costs.',
    },
  },
  {
    slug: 'ubumwe-furniture-works',
    business: 'Ubumwe Furniture Works',
    owner: 'Emmanuel Twagirayezu',
    role: 'Production Manager',
    industry: 'manufacturing',
    location: 'Masoro, Kigali',
    image: `${IMG}a4f4c3d3-2a3c-4598-9448-00bb4f94a87c.jpg`,
    alt: 'Emmanuel Twagirayezu inspecting finished goods in the workshop',
    products: ['purchases', 'inventory', 'expenses'],
    plan: 'Growth',
    staff: '22 staff',
    since: '2023',
    headline: 'How a workshop with eight suppliers stopped paying twice',
    result: 'RWF [X] saved on materials',
    summary: 'Orders, deliveries, and supplier payments now live in one place.',
    quote: 'We no longer pay twice for the same invoice.',
    results: [
      { value: 'RWF [X]', label: 'saved on materials' },
      { value: '[X]', label: 'suppliers managed' },
      { value: '[X]', label: 'duplicate payments' },
    ],
    challenge: ['With eight suppliers, invoices got lost and some were paid twice.'],
    solution: ['Every purchase order, delivery, and payment is recorded against the supplier in Kayko.'],
    outcome: ['Supplier balances are always clear, and the team negotiates better prices with real history.'],
    inlineImage: {
      src: `${IMG}ab0101ab-8ad6-4588-83d1-46af0a029122.jpg`,
      alt: 'Checking delivered materials in the store',
      caption: 'Deliveries are checked against purchase orders.',
    },
    screenshot: {
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Supplier balances',
          rows: [
            { label: 'Nyabugogo Timber', value: 'RWF 1,240,000', tone: 'warning' },
            { label: 'Kigali Hardware Supply', value: 'RWF 384,500' },
            { label: 'Rwanda Paints', value: 'Settled', tone: 'positive' },
          ],
        },
      },
      caption: 'Every supplier balance in one view.',
    },
  },
]
